import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { trackRead } from './firebaseUsageTracker';

// =============================================================================
// CACHE INVALIDATION SERVICE
// 
// This service allows the dev to remotely clear all users' local caches
// by incrementing a version number in Firestore. On app load, clients check
// the server version against their locally stored version - if different,
// they clear their localStorage and refresh state.
// =============================================================================

const LOCAL_CACHE_VERSION_KEY = 'blackjack-trainer-cache-version';

// All localStorage keys used by the app
const CACHE_KEYS_TO_CLEAR = [
  'blackjack-trainer-session',
  'blackjack-trainer-history',
  'blackjack-trainer-tutorial-dismissed',
];

export type CacheVersionInfo = {
  serverVersion: number;
  localVersion: number;
  wasInvalidated: boolean;
  lastInvalidated?: Date;
};

/**
 * Check the server's cache version against the locally stored version.
 * If the server version is higher, clear all local caches.
 * 
 * @returns Info about the cache check, including whether cache was invalidated
 */
export async function checkAndInvalidateCache(): Promise<CacheVersionInfo> {
  try {
    const configRef = doc(db, 'config', 'app');
    const configSnap = await getDoc(configRef);
    trackRead('config/app', 'cache-version-check');
    
    // If no config doc exists yet, nothing to invalidate
    if (!configSnap.exists()) {
      return {
        serverVersion: 0,
        localVersion: 0,
        wasInvalidated: false,
      };
    }
    
    const data = configSnap.data();
    const serverVersion = data?.cacheVersion ?? 0;
    const lastInvalidated = data?.lastInvalidated?.toDate?.() ?? undefined;
    
    // Get locally stored version
    let localVersion = 0;
    try {
      localVersion = parseInt(localStorage.getItem(LOCAL_CACHE_VERSION_KEY) || '0', 10);
    } catch {
      // Ignore localStorage errors
    }
    
    // If server version is higher, invalidate local cache
    if (serverVersion > localVersion) {
      clearLocalCaches();
      
      // Update local version to match server
      try {
        localStorage.setItem(LOCAL_CACHE_VERSION_KEY, String(serverVersion));
      } catch {
        // Ignore localStorage errors
      }
      
      return {
        serverVersion,
        localVersion,
        wasInvalidated: true,
        lastInvalidated,
      };
    }
    
    return {
      serverVersion,
      localVersion,
      wasInvalidated: false,
      lastInvalidated,
    };
  } catch (err) {
    console.warn('Failed to check cache version:', err);
    return {
      serverVersion: 0,
      localVersion: 0,
      wasInvalidated: false,
    };
  }
}

/**
 * Clear all localStorage keys used by the app.
 * Called when the server cache version is higher than local.
 */
function clearLocalCaches(): void {
  try {
    CACHE_KEYS_TO_CLEAR.forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('[CacheInvalidation] Local caches cleared due to version update');
  } catch (err) {
    console.warn('Failed to clear local caches:', err);
  }
}

/**
 * Get the current local cache version (for display in admin panel).
 */
export function getLocalCacheVersion(): number {
  try {
    return parseInt(localStorage.getItem(LOCAL_CACHE_VERSION_KEY) || '0', 10);
  } catch {
    return 0;
  }
}
